/**
 * Agent Detail Page — sub-tabs: Overview, Workflow, Test & Run, History, Versions, Metrics
 * Opened from Agent Hub when clicking an agent card.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  ArrowLeft,
  Bot,
  Zap,
  Play,
  Settings,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Activity,
  GitBranch,
  DollarSign,
  Network,
  Loader2,
  Pencil,
  ArrowUp,
  ArrowDown,
  FlaskConical,
  BarChart3,
  History,
  FileText,
  Layers,
  Brain,
} from 'lucide-react';
import { cpFetch } from '@/lib/cpClient';
import { AgentLifecyclePanel } from '../components/AgentLifecyclePanel';
import AgentWorkflowDesigner from '../components/AgentWorkflowDesigner';
import WorkflowTestRunner from '../components/WorkflowTestRunner';
import ModelComparison from '../components/ModelComparison';
import ExecutionHistory from '../volcano/components/ExecutionHistory';
import CostAnalytics from '../volcano/components/CostAnalytics';
import AgentExecutionModal from '../components/AgentExecutionModal';
import { countWorkflowSteps, getWorkflowSteps } from '../agentHonesty';
import { asDisplayText, asStringList, getAgentIntertwine, resolveAgentSlug } from '../agentIntertwine';
import { CpHttpError } from '@/lib/cpClient';
import { isTerminalStubAgent, agentStageLabel } from '../agentHonesty';
import { IntertwinePanel } from '../components/IntertwinePanel';

type SubTab = 'overview' | 'workflow' | 'test' | 'history' | 'lifecycle' | 'versions' | 'metrics';

const stageColors: Record<string, string> = {
  production: 'bg-emerald-100 text-emerald-700',
  staging: 'bg-amber-100 text-amber-700',
  draft: 'bg-gray-100 text-gray-600',
};

export default function AgentDetailPage() {
  const { agentId } = useParams<{ agentId: string }>();
  const navigate = useNavigate();
  const [agent, setAgent] = useState<any>(null);
  const [roster, setRoster] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<SubTab>('overview');
  const [executeOpen, setExecuteOpen] = useState(false);
  const [workflowOpen, setWorkflowOpen] = useState(false);
  const [versions, setVersions] = useState<any[]>([]);
  const [versionsLoading, setVersionsLoading] = useState(false);

  useEffect(() => {
    if (agentId) loadAgent();
  }, [agentId]);

  useEffect(() => {
    if (tab === 'versions' && agentId) loadVersions();
  }, [tab, agentId]);

  const loadAgent = async () => {
    try {
      setLoading(true);
      setError(null);
      const discoverP = cpFetch('/api/agents/discover').catch(() => ({ agents: [] }));
      let res: any = null;
      try {
        res = await cpFetch(`/api/agents/${encodeURIComponent(String(agentId))}`);
      } catch (err: any) {
        const discover = await discoverP;
        const list = Array.isArray(discover?.agents) ? discover.agents : [];
        const resolved = resolveAgentSlug(agentId, list);
        if (resolved && resolved !== agentId) {
          navigate(`/agents/hub/${encodeURIComponent(resolved)}`, { replace: true });
          return;
        }
        if (err instanceof CpHttpError && err.status === 404) {
          setAgent(null);
          setError('Agent not found');
          return;
        }
        throw err;
      }
      const loaded = res && typeof res === 'object' && res.agent && typeof res.agent === 'object'
        ? res.agent
        : (res && typeof res === 'object' && (res.agent_id || res.id) ? res : null);
      if (!loaded || typeof loaded !== 'object') {
        setAgent(null);
        setError('Agent not found');
        return;
      }
      const canonical = String(loaded.agent_id || loaded.id || '').trim();
      if (canonical && agentId && canonical !== agentId) {
        navigate(`/agents/hub/${encodeURIComponent(canonical)}`, { replace: true });
        return;
      }
      setAgent(loaded);
      const discover = await discoverP;
      setRoster(Array.isArray(discover?.agents) ? discover.agents : []);
    } catch (err: any) {
      setAgent(null);
      setError(err.message || 'Failed to load agent');
    } finally {
      setLoading(false);
    }
  };

  const loadVersions = async () => {
    try {
      setVersionsLoading(true);
      const res = await cpFetch(`/api/agents/${agentId}/versions`);
      setVersions(res.versions || []);
    } catch {
      setVersions([]);
    } finally {
      setVersionsLoading(false);
    }
  };

  const handlePromote = async () => {
    if (!agent) return;
    const target = agent.deployment_stage === 'draft' ? 'staging' : 'production';
    if (!confirm(`Promote agent to ${target}?`)) return;
    try {
      await cpFetch(`/api/agents/${agentId}/promote`, { method: 'POST', body: JSON.stringify({ target_stage: target }) });
      loadAgent();
    } catch (err: any) {
      alert(`Promote failed: ${err.message}`);
    }
  };

  const handleRollback = async () => {
    if (!confirm('Rollback to previous version?')) return;
    try {
      await cpFetch(`/api/agents/${agentId}/rollback`, { method: 'POST' });
      loadAgent();
    } catch (err: any) {
      alert(`Rollback failed: ${err.message}`);
    }
  };

  const handleWorkflowSave = () => loadAgent();

  /* ---------- Loading / Error states ---------- */

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400 mr-2" />
        <span className="text-gray-500">Loading agent...</span>
      </div>
    );
  }

  if (error || !agent) {
    return (
      <div className="p-6">
        <Button variant="ghost" size="sm" onClick={() => navigate('/agents/hub')} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Back
        </Button>
        <div className="bg-red-50 text-red-700 rounded-lg p-4">{error || 'Agent not found'}</div>
      </div>
    );
  }

  const isVolcano = agent.execution_engine === 'volcano' || !!agent.volcano_workflow || !!agent.volcano_config?.workflows;
  const workflowSteps = getWorkflowSteps(agent).filter((step) => step && typeof step === 'object');
  const stepCount = countWorkflowSteps(agent);
  const id = agent.agent_id || agent.id || agentId;
  const capabilities = asStringList(agent.capabilities);
  const toolsUsed = asStringList(agent.tools_used);
  const dependsOn = asStringList(agent.depends_on);
  const rulesText = asDisplayText(agent.rules_of_engagement);
  const intertwine = getAgentIntertwine(agent, roster);
  const stub = isTerminalStubAgent(agent);
  const stageLabel = agentStageLabel(agent);
  const agentNameMap = new Map<string, string>(
    roster.map((row: any) => {
      const rowId = String(row?.agent_id || row?.id || '');
      return [rowId, String(row?.name || rowId)];
    }).filter(([rowId]) => rowId),
  );
  const firstStep = workflowSteps[0] || {};

  const subTabs: { id: SubTab; label: string; icon: React.ElementType }[] = [
    { id: 'overview', label: 'Overview', icon: FileText },
    { id: 'workflow', label: 'Workflow', icon: Layers },
    { id: 'test', label: 'Test & Run', icon: FlaskConical },
    { id: 'history', label: 'History', icon: History },
    { id: 'lifecycle', label: 'Lifecycle', icon: Brain },
    { id: 'versions', label: 'Versions', icon: GitBranch },
    { id: 'metrics', label: 'Metrics', icon: BarChart3 },
  ];

  return (
    <div className="h-full flex flex-col">
      {/* ── Header ───────────────────────────────────────────────── */}
      <div className="border-b bg-white px-6 py-4">
        <div className="flex items-center gap-3 mb-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/agents/hub')} className="mr-1">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${isVolcano ? 'bg-amber-100' : 'bg-gray-100'}`}>
            {isVolcano ? <Zap className="w-5 h-5 text-amber-600" /> : <Bot className="w-5 h-5 text-gray-500" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="text-lg font-semibold text-gray-900 truncate">{agent.name}</h1>
              <Badge className={`text-xs ${stub ? 'bg-amber-100 text-amber-800' : (stageColors[agent.deployment_stage] || stageColors.draft)}`}>
                {stageLabel}
              </Badge>
              {isVolcano && (
                <Badge variant="secondary" className="text-xs bg-amber-50 text-amber-700">
                  <Zap className="w-3 h-3 mr-0.5" /> Volcano · {stepCount} steps
                </Badge>
              )}
            </div>
            <p className="text-xs text-gray-500 truncate">{id} · v{agent.version || '1.0.0'} · {agent.domain || 'general'}</p>
          </div>

          <div className="flex items-center gap-2">
            {agent.deployment_stage !== 'production' && (
              <Button size="sm" variant="outline" onClick={handlePromote}>
                <ArrowUp className="w-3.5 h-3.5 mr-1" /> Promote
              </Button>
            )}
            {agent.deployment_stage === 'production' && (
              <Button size="sm" variant="outline" onClick={handleRollback}>
                <ArrowDown className="w-3.5 h-3.5 mr-1" /> Rollback
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => setTab('workflow')}>
              <Pencil className="w-3.5 h-3.5 mr-1" /> Edit steps
            </Button>
            <Button size="sm" className="bg-violet-600 hover:bg-violet-700" onClick={() => setExecuteOpen(true)}>
              <Play className="w-3.5 h-3.5 mr-1" /> Run
            </Button>
          </div>
        </div>

        {/* Sub-tabs */}
        <nav className="flex gap-1 -mb-4 pb-0">
          {subTabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                tab === t.id
                  ? 'border-violet-600 text-violet-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <t.icon className="w-3.5 h-3.5" />
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      {/* ── Tab Content ─────────────────────────────────────────── */}
      <div className="flex-1 overflow-auto bg-gray-50 p-6">

        {/* Overview */}
        {tab === 'overview' && (
          <div className="space-y-6 max-w-4xl">
            <Card>
              <CardHeader><CardTitle className="text-base">Agent Details</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500 text-xs mb-1">Description</p>
                    <p className="text-gray-900">{agent.description || '—'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-xs mb-1">Domain</p>
                    <p className="text-gray-900">{agent.domain || '—'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-xs mb-1">Type</p>
                    <p className="text-gray-900">{agent.agent_type || '—'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-xs mb-1">Engine</p>
                    <p className="text-gray-900">{isVolcano ? 'Volcano' : 'Legacy'}</p>
                  </div>
                </div>

                {capabilities.length > 0 && (
                  <div>
                    <p className="text-gray-500 text-xs mb-1.5">Capabilities</p>
                    <div className="flex flex-wrap gap-1.5">
                      {capabilities.map((c: string) => (
                        <Badge key={c} variant="secondary" className="text-xs">{c}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {toolsUsed.length > 0 && (
                  <div>
                    <p className="text-gray-500 text-xs mb-1.5">Tools Used</p>
                    <div className="flex flex-wrap gap-1.5">
                      {toolsUsed.map((t: string) => (
                        <Badge key={t} variant="outline" className="text-xs font-mono">{t}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {dependsOn.length > 0 && (
                  <div>
                    <p className="text-gray-500 text-xs mb-1.5">Depends On</p>
                    <div className="flex flex-wrap gap-1.5">
                      {dependsOn.map((d: string) => (
                        <Badge key={d} variant="outline" className="text-xs cursor-pointer hover:bg-violet-50"
                          onClick={() => navigate(`/agents/hub/${d}`)}
                        >{d}</Badge>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Network className="w-4 h-4" /> Intertwined
                </CardTitle>
              </CardHeader>
              <CardContent>
                <IntertwinePanel
                  intertwine={intertwine}
                  agentNameMap={agentNameMap}
                  onOpenAgent={(otherId) => navigate(`/agents/hub/${otherId}`)}
                />
                <Button
                  size="sm"
                  variant="outline"
                  className="mt-4"
                  onClick={() => navigate(`/agents/ecosystem?agent=${encodeURIComponent(String(id))}`)}
                >
                  <Network className="w-3.5 h-3.5 mr-1" /> View in graph
                </Button>
              </CardContent>
            </Card>

            {/* SLA */}
            {agent.sla && (
              <Card>
                <CardHeader><CardTitle className="text-base">SLA Guardrails</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    {agent.sla.max_latency_ms && (
                      <div>
                        <p className="text-gray-500 text-xs mb-1">Max Latency</p>
                        <p className="text-gray-900 font-medium">{agent.sla.max_latency_ms}ms</p>
                      </div>
                    )}
                    {agent.sla.max_cost_per_run && (
                      <div>
                        <p className="text-gray-500 text-xs mb-1">Max Cost / Run</p>
                        <p className="text-gray-900 font-medium">${agent.sla.max_cost_per_run}</p>
                      </div>
                    )}
                    {agent.sla.min_success_rate && (
                      <div>
                        <p className="text-gray-500 text-xs mb-1">Min Success Rate</p>
                        <p className="text-gray-900 font-medium">{agent.sla.min_success_rate}%</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* I/O Schemas */}
            {(agent.input_schema || agent.output_schema) && (
              <Card>
                <CardHeader><CardTitle className="text-base">Contract</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    {agent.input_schema && (
                      <div>
                        <p className="text-gray-500 text-xs mb-1.5">Input Schema</p>
                        <pre className="text-xs bg-gray-100 rounded p-3 overflow-auto max-h-48 font-mono">
                          {JSON.stringify(agent.input_schema, null, 2)}
                        </pre>
                      </div>
                    )}
                    {agent.output_schema && (
                      <div>
                        <p className="text-gray-500 text-xs mb-1.5">Output Schema</p>
                        <pre className="text-xs bg-gray-100 rounded p-3 overflow-auto max-h-48 font-mono">
                          {JSON.stringify(agent.output_schema, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Rules of Engagement */}
            {rulesText && (
              <Card>
                <CardHeader><CardTitle className="text-base">Rules of Engagement</CardTitle></CardHeader>
                <CardContent>
                  <pre className="text-sm bg-gray-50 rounded p-4 whitespace-pre-wrap">
                    {rulesText}
                  </pre>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Workflow */}
        {tab === 'workflow' && (
          <div className="space-y-4">
            {isVolcano ? (
              <>
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-600">
                    Numbered Workflow Steps are the durable artifact. Designer writes into these steps — not a chat transcript.
                  </p>
                  <Button size="sm" variant="outline" onClick={() => setWorkflowOpen(true)}>
                    <Pencil className="w-3.5 h-3.5 mr-1" /> Open Designer
                  </Button>
                </div>

                {/* Inline step summary — source of truth */}
                <Card>
                  <CardHeader><CardTitle className="text-base">Workflow Steps ({stepCount})</CardTitle></CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {workflowSteps.map((step: any, i: number) => (
                        <div key={step.name || step.id || i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                          <div className="w-7 h-7 rounded-full bg-violet-100 text-violet-700 text-xs font-bold flex items-center justify-center">
                            {step.step_number || i + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900">{step.name || `Step ${i + 1}`}</p>
                            <p className="text-xs text-gray-500">{step.type || step.provider || 'unknown'}{step.model ? ` · ${step.model}` : ''}{step.version ? ` · v${step.version}` : ''}</p>
                          </div>
                          <Badge variant="outline" className="text-xs">
                            {step.type || step.provider || '?'}
                          </Badge>
                        </div>
                      ))}
                      {workflowSteps.length === 0 && (
                        <p className="text-sm text-gray-500">No numbered workflow steps on this agent.</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <Layers className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                <p className="font-medium">Legacy Agent</p>
                <p className="text-sm mt-1">This agent uses the legacy execution engine and has no visual workflow.</p>
              </div>
            )}
          </div>
        )}

        {/* Test & Run */}
        {tab === 'test' && (
          <div className="space-y-4">
            {isVolcano ? (
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                <Card>
                  <CardHeader><CardTitle className="text-base">Execute Agent</CardTitle></CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-600 mb-4">
                      Run this agent with custom input. Use the Workflow Designer for step-by-step animated execution.
                    </p>
                    <div className="flex gap-2">
                      <Button size="sm" className="bg-violet-600 hover:bg-violet-700" onClick={() => setExecuteOpen(true)}>
                        <Play className="w-3.5 h-3.5 mr-1" /> Quick Run
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setWorkflowOpen(true)}>
                        <FlaskConical className="w-3.5 h-3.5 mr-1" /> Animated Test in Designer
                      </Button>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader><CardTitle className="text-base">Model Comparison</CardTitle></CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-600 mb-3">
                      Compare LLM models on a specific step to find the best performer.
                    </p>
                    <ModelComparison
                      step={firstStep}
                      input={{ test: true, message: 'Test from Agent Detail' }}
                      onComplete={() => {}}
                    />
                  </CardContent>
                </Card>
              </div>
            ) : (
              <Card>
                <CardContent className="py-8 text-center text-gray-500">
                  <FlaskConical className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                  <p className="font-medium">Quick Run</p>
                  <Button size="sm" className="mt-3" onClick={() => setExecuteOpen(true)}>
                    <Play className="w-3.5 h-3.5 mr-1" /> Execute Agent
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* History */}
        {tab === 'history' && (
          <ExecutionHistory agentId={id} agentName={agent.name} />
        )}

        {/* Lifecycle */}
        {tab === 'lifecycle' && (
          <AgentLifecyclePanel agentId={id} agent={agent} />
        )}

        {/* Versions */}
        {tab === 'versions' && (
          <div className="space-y-4 max-w-3xl">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <GitBranch className="w-4 h-4" /> Version History
                </CardTitle>
              </CardHeader>
              <CardContent>
                {versionsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                  </div>
                ) : versions.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-6">No version history available. Current version: {agent.version || '1.0.0'}</p>
                ) : (
                  <div className="space-y-2">
                    {versions.map((v: any, i: number) => (
                      <div key={v.version || i} className={`flex items-center justify-between p-3 rounded-lg border ${v.is_active ? 'bg-violet-50 border-violet-200' : 'bg-white'}`}>
                        <div className="flex items-center gap-3">
                          <Badge variant={v.is_active ? 'default' : 'secondary'} className="text-xs">
                            v{v.version}
                          </Badge>
                          <div>
                            <p className="text-sm text-gray-700">{v.release_notes || 'No release notes'}</p>
                            <p className="text-xs text-gray-500">{new Date(v.created_at).toLocaleString()}</p>
                          </div>
                        </div>
                        {v.is_active && <Badge className="bg-emerald-100 text-emerald-700 text-xs">Active</Badge>}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Metrics */}
        {tab === 'metrics' && (
          <CostAnalytics agentId={id} />
        )}
      </div>

      {/* ── Modals ──────────────────────────────────────────────── */}
      {executeOpen && (
        <AgentExecutionModal
          agent={agent}
          isOpen={executeOpen}
          onClose={() => setExecuteOpen(false)}
        />
      )}

      {workflowOpen && isVolcano && (
        <AgentWorkflowDesigner
          agent={agent}
          isOpen={workflowOpen}
          onClose={() => setWorkflowOpen(false)}
          onSave={handleWorkflowSave}
        />
      )}
    </div>
  );
}
